import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Coins, Sparkles, ArrowLeft, Check, History, DollarSign, Undo2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import Navigation from "@/components/Navigation";
import { fireCoinsConfetti } from "@/lib/confetti";
import { useLocale } from "@/i18n/LocaleContext";
import PayPalEmailWarning from "@/components/PayPalEmailWarning";
import { useAppMode } from "@/context/AppModeContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function Credits() {
  const navigate = useNavigate();
  const { user, updateUser, checkAuth } = useAuth();
  const { t, fm } = useLocale();
  const { isEarner, isDluxe } = useAppMode();
  const [packages, setPackages] = useState([]);
  const [costs, setCosts] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [refundMap, setRefundMap] = useState({});
  const [refundDialog, setRefundDialog] = useState(null);
  const [refundReason, setRefundReason] = useState("");
  const [submittingRefund, setSubmittingRefund] = useState(false);
  const paypalContainerRef = useRef(null);
  const paypalButtonRendered = useRef(false);

  const fetchData = useCallback(async () => {
    try {
      const [packagesRes, costsRes, historyRes, refundsRes] = await Promise.all([
        api.get("/credits/packages"),
        api.get("/credits/costs"),
        api.get("/credits/history"),
        api.get("/refund-requests/mine")
      ]);
      setPackages(packagesRes.data.packages);
      setCosts(costsRes.data.costs);
      setHistory(historyRes.data.transactions);
      const rm = {};
      (refundsRes.data.requests || []).forEach((r) => {
        if (!rm[r.transaction_id] || r.status === "pending") rm[r.transaction_id] = r.status;
      });
      setRefundMap(rm);
    } catch (error) {
      console.error("Failed to load credit data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Check for payment success from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    if (paymentStatus === 'success') {
      toast.success("Payment successful! Credits added to your account.");
      fireCoinsConfetti();
      checkAuth(); // Refresh user data
      // Clean URL
      window.history.replaceState({}, '', '/credits');
    } else if (paymentStatus === 'cancelled') {
      toast.error("Payment cancelled");
      window.history.replaceState({}, '', '/credits');
    }
  }, [fetchData, checkAuth]);

  // Map package IDs to PayPal button IDs
  const paypalButtonMap = {
    "pkg_50": "J5GKLZW7H9Q9L",      // Starter $4.99
    "pkg_150": "KRW28UP9LGDJY",     // Popular $9.99
    "pkg_500": "M2PF9D7BM7WYU"      // Premium $24.99
  };
  
  // Get the PayPal button ID and container ID for the selected package
  const paypalButtonId = selectedPackage ? paypalButtonMap[selectedPackage.id] : null;
  const paypalContainerId = paypalButtonId ? `js-sdk-container-${paypalButtonId}` : null;

  // Initialize PayPal Hosted Button when payment dialog opens
  useEffect(() => {
    if (paymentDialog && selectedPackage && window.paypal && paypalButtonId && !paypalButtonRendered.current) {
      // Clear previous button if exists
      if (paypalContainerRef.current) {
        paypalContainerRef.current.innerHTML = '';
      }
      
      // Render PayPal hosted button
      try {
        window.paypal.HostedButtons({
          hostedButtonId: paypalButtonId,
        }).render(`#${paypalContainerId}`);
        paypalButtonRendered.current = true;
      } catch (error) {
        console.error("PayPal button render error:", error);
      }
    }
    
    // Reset rendered flag when dialog closes
    if (!paymentDialog) {
      paypalButtonRendered.current = false;
    }
  }, [paymentDialog, selectedPackage, paypalButtonId, paypalContainerId]);

  const handlePurchase = useCallback(async (paymentMethod) => {
    if (!selectedPackage) return;
    
    setPaymentDialog(false);
    setProcessingPayment(true);

    try {
      const response = await api.post("/credits/purchase-direct", {
        package_id: selectedPackage.id,
        payment_method: paymentMethod
      });
      
      // If Stripe or PayPal, redirect to checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        // Demo payment - instant credit addition
        toast.success(`${response.data.credits_added} credits added to your account!`);
        fireCoinsConfetti();
        await checkAuth(); // Refresh user data
        fetchData(); // Refresh history
        setSelectedPackage(null);
        setProcessingPayment(false);
      }
    } catch (error) {
      console.error("Payment processing error:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Payment processing failed. Please try again.";
      toast.error(errorMessage);
      setProcessingPayment(false);
    }
  }, [selectedPackage, checkAuth, fetchData]);

  const submitRefundRequest = async () => {
    if (!refundDialog || submittingRefund || refundReason.trim().length < 5) return;
    setSubmittingRefund(true);
    try {
      await api.post("/refund-requests", { transaction_id: refundDialog, kind: "credit", reason: refundReason.trim() });
      toast.success(t("credits.refundSent"));
      setRefundMap((m) => ({ ...m, [refundDialog]: "pending" }));
      setRefundDialog(null);
      setRefundReason("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send request");
    } finally {
      setSubmittingRefund(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="credits-page">
      <Navigation />
      
      <main className="pt-20 pb-24 px-4 md:px-6">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            className="mb-6 text-muted-foreground hover:text-white -ml-2"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("common.back")}
          </Button>

          {/* Current balance */}
          <div className="glass-card p-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">{t("common.yourBalance")}</p>
                <p className="text-4xl font-bold font-['Outfit'] bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent pb-1">
                  {user?.credits || 0} {t("common.credits")}
                </p>
              </div>
              <div className="w-16 h-16 rounded-2xl gradient-gold flex items-center justify-center">
                <Coins className="w-8 h-8 text-black" />
              </div>
            </div>
          </div>

          {isEarner && (
            <div className="glass-card p-4 mb-8 border border-secondary/20 bg-secondary/5" data-testid="earner-credits-note">
              <p className="text-sm text-muted-foreground">{t("credits.earnerNote")}</p>
            </div>
          )}

          {/* Credit costs info */}
          <div className="glass-card p-6 mb-8">
            <h3 className="font-semibold mb-4">{t("credits.costs")}</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("credits.startChat")}</span>
                <span className="font-medium">{costs.chat_initiation || 10} {t("common.credits").toLowerCase()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("credits.sendMessage")}</span>
                <span className="font-medium">{costs.message || 5} {t("common.credits").toLowerCase()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("credits.requestMeetup")}</span>
                <span className="font-medium">{costs.meetup_request || 25} {t("common.credits").toLowerCase()}</span>
              </div>
            </div>
          </div>

          {/* Toggle between packages and history */}
          <div className="flex gap-4 mb-6">
            <Button
              variant={!showHistory ? "default" : "outline"}
              className={!showHistory ? "bg-primary" : "border-white/10"}
              onClick={() => setShowHistory(false)}
              data-testid="packages-tab"
            >
              <Coins className="w-4 h-4 mr-2" />
              {t("credits.buy")}
            </Button>
            <Button
              variant={showHistory ? "default" : "outline"}
              className={showHistory ? "bg-primary" : "border-white/10"}
              onClick={() => setShowHistory(true)}
              data-testid="history-tab"
            >
              <History className="w-4 h-4 mr-2" />
              {t("credits.history")}
            </Button>
          </div>

          {!showHistory ? (
            /* Credit Packages */
            <div className="space-y-4">
              <h2 className="text-xl font-bold font-['Outfit']">{t("credits.buy")}</h2>
              
              <div className="grid gap-4">
                {packages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className={`glass-card p-6 cursor-pointer transition-all ${
                      selectedPackage?.id === pkg.id 
                        ? "border-secondary ring-2 ring-secondary/20" 
                        : "hover:border-white/20"
                    }`}
                    onClick={() => setSelectedPackage(pkg)}
                    data-testid={`package-${pkg.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl gradient-gold flex items-center justify-center">
                          <Coins className="w-7 h-7 text-black" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold font-['Outfit']">{pkg.name}</h3>
                            {pkg.popular && (
                              <span className="px-2 py-0.5 rounded-full bg-secondary/20 text-secondary text-xs font-medium">
                                {t("credits.bestValue")}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{pkg.credits} {t("common.credits")}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold font-['Outfit']">${pkg.price}</p>
                        <p className="text-xs text-muted-foreground">
                          ${(pkg.price / pkg.credits).toFixed(2)}/credit
                        </p>
                        {fm(pkg.price) && (
                          <p className="text-[11px] text-primary/80 mt-0.5" data-testid={`package-local-price-${pkg.id}`}>
                            ≈ {fm(pkg.price)} · {t("credits.billedUsd")}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {selectedPackage?.id === pkg.id && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <Button
                          className="w-full bg-secondary hover:bg-secondary/90"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPaymentDialog(true);
                          }}
                          disabled={processingPayment}
                        >
                          {processingPayment ? t("credits.processing") : t("credits.continuePayment")}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Transaction History */
            <div className="space-y-4">
              <h2 className="text-xl font-bold font-['Outfit']">{t("credits.history")}</h2>
              
              {history.length === 0 ? (
                <div className="glass-card p-8 text-center">
                  <History className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">{t("credits.noHistory")}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((transaction) => {
                    const tid = transaction.transaction_id || transaction.id;
                    const refundable = transaction.credits > 0 && !(transaction.type || "").includes("refund");
                    const reqStatus = refundMap[tid];
                    return (
                      <div key={tid} className="glass-card p-4" data-testid={`history-row-${tid}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{transaction.description}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(transaction.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-secondary">{transaction.credits > 0 ? "+" : ""}{transaction.credits} credits</p>
                            {transaction.amount != null && <p className="text-sm text-muted-foreground">${transaction.amount}</p>}
                          </div>
                        </div>
                        {refundable && (
                          <div className="mt-2 pt-2 border-t border-white/5 flex justify-end">
                            {transaction.refund_action === "approved" ? (
                              <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-400" data-testid={`refunded-chip-${tid}`}>{t("credits.refunded")}</span>
                            ) : transaction.refund_action === "denied" || reqStatus === "denied" ? (
                              <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400" data-testid={`refund-denied-chip-${tid}`}>{t("credits.refundDeniedChip")}</span>
                            ) : reqStatus === "pending" ? (
                              <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400" data-testid={`refund-pending-chip-${tid}`}>{t("credits.refundRequested")}</span>
                            ) : (
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-white transition-colors"
                                onClick={() => setRefundDialog(tid)}
                                data-testid={`request-refund-btn-${tid}`}
                              >
                                <Undo2 className="w-3 h-3" /> {t("credits.requestRefund")}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Payment Method Dialog */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">{t("credits.completePayment")}</DialogTitle>
            <DialogDescription>
              Purchase {selectedPackage?.credits} credits for ${selectedPackage?.price}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            <PayPalEmailWarning />
            {/* PayPal Hosted Button (includes PayPal & Venmo) */}
            <div className="space-y-2">
              <div 
                ref={paypalContainerRef}
                id={paypalContainerId}
                className="w-full min-h-[50px]"
              ></div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-4">
            All payments are secure and encrypted. Pay with PayPal or Venmo.
          </p>
        </DialogContent>
      </Dialog>

      {/* Refund Request Dialog */}
      <Dialog open={!!refundDialog} onOpenChange={(o) => !o && setRefundDialog(null)}>
        <DialogContent className="bg-card border-white/10 max-w-sm" data-testid="refund-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">{t("credits.refundDialogTitle")}</DialogTitle>
            <DialogDescription>{t("credits.refundDialogDesc")}</DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder={t("credits.refundReasonPh")}
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            rows={3}
            className="bg-white/5 border-white/10"
            data-testid="refund-reason-input"
          />
          <Button
            className="w-full bg-secondary hover:bg-secondary/90"
            onClick={submitRefundRequest}
            disabled={submittingRefund || refundReason.trim().length < 5}
            data-testid="refund-submit-btn"
          >
            {submittingRefund ? t("credits.processing") : t("credits.refundSubmit")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
